import { useState } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";

const EMPTY_FORM = {
  code: "",
  description: "",
  discountType: "percentage",
  discountValue: "",
  minOrderAmount: "",
  maxUses: "",
  startDate: "",
  endDate: "",
  active: true,
};

function toFormValues(coupon) {
  return {
    code: coupon.code ?? "",
    description: coupon.description ?? "",
    discountType: coupon.discountType ?? "percentage",
    discountValue: coupon.discountValue ?? "",
    minOrderAmount: coupon.minOrderAmount ?? "",
    maxUses: coupon.maxUses ?? "",
    startDate: coupon.startDate ? coupon.startDate.slice(0, 10) : "",
    endDate: coupon.endDate ? coupon.endDate.slice(0, 10) : "",
    active: coupon.active ?? true,
  };
}

// Crea o edita un cupón — si llega `coupon` entra en modo edición (PUT), si no,
// crea uno nuevo (POST). Si llega `isAdmin` + `companies`, agrega el selector de
// tienda ("Todas las tiendas" = cupón de plataforma, companyId null); el panel de
// tienda nunca ve ese selector — su cupón siempre es de su propia tienda, el
// backend ya lo fuerza del lado del servidor, no hace falta mandarlo acá.
export default function CreateCouponModal({ coupon, isAdmin = false, companies = [], onClose, onSaved }) {
  const isEdit = !!coupon;
  const [form, setForm] = useState(() => (coupon ? toFormValues(coupon) : EMPTY_FORM));
  const [companyId, setCompanyId] = useState(coupon?.companyId ?? "");
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discountValue) {
      toast.error("Código y descuento son obligatorios");
      return;
    }

    const payload = {
      ...form,
      code: form.code.toUpperCase(),
      discountValue: Number(form.discountValue),
      minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : 0,
      maxUses: form.maxUses ? Number(form.maxUses) : null,
      ...(isAdmin ? { companyId: companyId || null } : {}),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await API.put(`/coupons/${coupon.id}`, payload);
        toast.success("Cupón actualizado");
      } else {
        await API.post("/coupons", payload);
        toast.success("Cupón creado");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error guardando el cupón");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-6 overflow-y-auto max-h-[90vh]">
        <h3 className="text-xl font-bold mb-4 text-center">{isEdit ? "Editar cupón" : "Crear cupón"}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdmin && (
            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Tienda</label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">🌐 Todas las tiendas (cupón de plataforma)</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <input
            name="code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="Código del cupón (PROMO10)"
            className="w-full border rounded-lg px-3 py-2"
          />

          <input
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Descripción (opcional)"
            className="w-full border rounded-lg px-3 py-2"
          />

          <select
            name="discountType"
            value={form.discountType}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="percentage">Porcentaje (%)</option>
            <option value="fixed">Valor fijo ($)</option>
          </select>

          <input
            name="discountValue"
            type="number"
            value={form.discountValue}
            onChange={handleChange}
            placeholder={form.discountType === "percentage" ? "Ej: 10 (%)" : "Ej: 5000 ($)"}
            className="w-full border rounded-lg px-3 py-2"
          />

          <input
            name="minOrderAmount"
            type="number"
            value={form.minOrderAmount}
            onChange={handleChange}
            placeholder="Monto mínimo de compra (opcional)"
            className="w-full border rounded-lg px-3 py-2"
          />

          <input
            name="maxUses"
            type="number"
            value={form.maxUses}
            onChange={handleChange}
            placeholder="Máximo de usos (opcional)"
            className="w-full border rounded-lg px-3 py-2"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              name="startDate"
              type="date"
              value={form.startDate}
              onChange={handleChange}
              className="border rounded-lg px-3 py-2"
            />
            <input
              name="endDate"
              type="date"
              value={form.endDate}
              onChange={handleChange}
              className="border rounded-lg px-3 py-2"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="active"
              checked={form.active}
              onChange={handleChange}
            />
            Cupón activo
          </label>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 border rounded-lg py-2">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-supernova-500 text-white rounded-lg py-2 disabled:opacity-60">
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear cupón"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
